"use client";

import { HEADER_CONSTANTS, NAV_ITEMS } from "@/constants/header";
import type { NavItem, SubMenuItem } from "@/types";
import CloseIcon from "@public/svgs/close.svg";
import HamburgerIcon from "@public/svgs/hamburger.svg";
import RightArrowIcon from "@public/svgs/right_arrow.svg";
import Image from "next/image";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useEffect, useState } from "react";

/**
 * 스크롤 및 리사이즈 감지 커스텀 훅
 *
 * 헤더의 스크롤 상태와 화면 크기 변화를 감지하여
 * 적절한 스타일과 동작을 제어합니다.
 *
 * @returns 스크롤 및 화면 크기 상태 객체
 */
function useScrollAndResize() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isSmallScreen, setIsSmallScreen] = useState(false);
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);

    /**
     * 스크롤 이벤트 핸들러
     *
     * 스크롤 위치가 50px을 넘으면 헤더 배경을 표시합니다.
     */
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    /**
     * 리사이즈 이벤트 핸들러
     *
     * 화면 크기에 따라 모바일과 작은 화면 상태를 업데이트합니다.
     */
    const handleResize = () => {
      const width = window.innerWidth;
      setIsMobile(width < 1024);
      setIsSmallScreen(width < 640);
    };

    // 초기 상태 설정
    handleScroll();
    handleResize();

    window.addEventListener("scroll", handleScroll);
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  // 서버 사이드 렌더링에서는 항상 false를 반환하고,
  // 클라이언트 사이드에서만 실제 상태를 사용
  return {
    isScrolled: isMounted ? isScrolled : false,
    isMobile: isMounted ? isMobile : false,
    isSmallScreen: isMounted ? isSmallScreen : false,
  };
}

/**
 * 서브메뉴 컴포넌트
 *
 * 모바일 메뉴에서 사용되는 서브메뉴를 렌더링합니다.
 * 각 서브메뉴 아이템에 화살표 아이콘과 호버 효과를 제공합니다.
 *
 * @param items - 서브메뉴 아이템 배열
 * @param onLinkClick - 링크 클릭 시 실행할 함수
 */
function SubMenu({
  items,
  onLinkClick,
}: {
  items: SubMenuItem[];
  isVisible: boolean;
  onLinkClick: () => void;
}) {
  return (
    <div className="space-y-3">
      {items.map((subItem) => (
        <div key={subItem.href} className="flex items-center mb-4">
          <Link
            href={subItem.href}
            onClick={onLinkClick}
            className="group flex-1 flex items-center py-2 text-foreground transition-colors duration-300 font-semibold text-lg"
          >
            <span className="flex-1">{subItem.label}</span>
            <RightArrowIcon className="inline-block shrink-0 w-4 h-4 text-border group-hover:text-primary group-hover:translate-x-1 transition-all duration-100" />
          </Link>
        </div>
      ))}
    </div>
  );
}

/**
 * 드롭다운 메뉴 컴포넌트
 *
 * 데스크톱 헤더에서 사용되는 드롭다운 메뉴를 렌더링합니다.
 * 호버 시 나타나며, 서브메뉴 아이템들을 표시합니다.
 *
 * @param item - 네비게이션 아이템
 * @param isVisible - 드롭다운 표시 여부
 */
function DropdownMenu({
  item,
  isVisible,
}: {
  item: NavItem;
  isVisible: boolean;
}) {
  if (!item.submenu) return null;

  return (
    <div
      className={`absolute top-full left-0 border-b-8 w-70 border-primary bg-background shadow-lg rounded-b-2xl py-4 z-50 transition-all duration-300 ease-out ${
        isVisible
          ? "opacity-100 translate-y-0 pointer-events-auto"
          : "opacity-0 -translate-y-2 pointer-events-none"
      }`}
    >
      <div className="flex flex-col gap-1">
        {item.submenu.map((subItem) => (
          <Link
            key={subItem.href}
            href={subItem.href}
            className="flex gap-4 items-center justify-between px-9 py-3 group"
          >
            <p className="shrink-0 text-xl font-bold text-foreground group-hover:text-primary transition-colors duration-100">
              {subItem.label}
            </p>
            <RightArrowIcon className="inline-block shrink-0 w-4 h-4 text-border group-hover:text-primary group-hover:translate-x-1 transition-all duration-100" />
          </Link>
        ))}
      </div>
    </div>
  );
}

/**
 * 웹사이트 헤더 컴포넌트
 *
 * 웹사이트 상단에 표시되는 헤더로, 로고, 네비게이션 메뉴, 모바일 메뉴를 포함합니다.
 * 스크롤 상태와 화면 크기에 따라 동적으로 스타일이 변경되며,
 * 반응형 디자인을 지원합니다.
 *
 * 주요 기능:
 * - 충북대학교 RISE사업단 로고 및 홈 링크
 * - 데스크톱 네비게이션 메뉴 (호버 드롭다운)
 * - 모바일 햄버거 메뉴
 * - 스크롤 시 배경색 변경
 * - 반응형 디자인 (모바일/태블릿/데스크톱)
 * - 접근성 지원 (aria-label, role 등)
 */
export function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [hoveredMenu, setHoveredMenu] = useState<string | null>(null);
  const pathname = usePathname();
  const { isMobile, isScrolled, isSmallScreen } = useScrollAndResize();

  // 404 페이지 및 스크롤 상태에 따른 헤더 배경 표시 여부 결정
  const is404Page =
    typeof window !== "undefined" &&
    ![
      "/",
      "/greeting",
      "/location",
      "/organization",
      "/announcements",
      "/notices",
      "/resources",
      "/recruit",
      "/newsletters",
    ].includes(pathname) &&
    !pathname.startsWith("/newsletters/") &&
    !pathname.startsWith("/recruit/") &&
    !pathname.startsWith("/notices/") &&
    !pathname.startsWith("/resources/") &&
    !pathname.startsWith("/announcements/");

  // 서버 사이드 렌더링에서는 기본적으로 배경을 표시하지 않음
  // 클라이언트 사이드에서 실제 상태에 따라 배경 표시 여부 결정
  // 메인 페이지에서도 항상 배경 표시
  const shouldShowBackground =
    typeof window !== "undefined"
      ? isSmallScreen
        ? isMenuOpen
        : true // 항상 배경 표시
      : true; // 서버 사이드에서도 배경 표시

  /**
   * 모바일 메뉴 열림/닫힘에 따른 body 스크롤 제어
   *
   * 모바일에서 메뉴가 열려있을 때 페이지 스크롤을 방지합니다.
   */
  useEffect(() => {
    if (isMobile && isMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isMobile, isMenuOpen]);

  /**
   * 메뉴 토글 함수
   *
   * 모바일 메뉴의 열기/닫기 상태를 전환합니다.
   */
  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);

  /**
   * 메뉴 닫기 함수
   *
   * 모바일 메뉴를 닫습니다.
   */
  const closeMenu = () => setIsMenuOpen(false);

  /**
   * 텍스트 색상 결정 함수
   *
   * 현재 상태에 따라 적절한 텍스트 색상을 반환합니다.
   *
   * @param isHover - 호버 상태 여부
   * @returns 텍스트 색상 클래스
   */
  const getTextColor = (isHover: boolean = false) => {
    if (isHover) return "text-primary";
    return shouldShowBackground ? "text-foreground" : "text-white";
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-${
        HEADER_CONSTANTS.ANIMATION_DURATION.HEADER
      } ${
        typeof window !== "undefined" && shouldShowBackground
          ? "bg-background/95 backdrop-blur-sm border-b border-border shadow-sm"
          : "bg-background/95 backdrop-blur-sm border-b border-border shadow-sm"
      } ${
        isMenuOpen && isMobile
          ? HEADER_CONSTANTS.MENU.MOBILE_FULL_HEIGHT
          : "h-auto"
      }`}
    >
      <div className="max-w-[1540px] h-full mx-auto px-5 flex flex-col">
        {/* 상단 헤더 영역: 로고 및 네비게이션 */}
        <div className="flex items-center justify-between h-18 sm:h-23 flex-shrink-0">
          {/* 로고 및 브랜드명 */}
          <div className="shrink-0 scale-75 sm:scale-100 origin-left">
            <Link
              href="/"
              onClick={closeMenu}
              className="flex items-center"
              aria-label="홈으로 이동"
            >
              <Image
                src="/logo_header.png"
                alt="충북대학교 RISE사업단 로고"
                width={HEADER_CONSTANTS.STYLES.LOGO_WIDTH}
                height={70}
                className={`h-[60px] -ml-1.5 w-auto transition-all duration-${HEADER_CONSTANTS.ANIMATION_DURATION.HEADER}`}
                priority
              />
            </Link>
          </div>

          {/* 데스크톱 네비게이션 메뉴 */}
          <nav
            className="hidden xl:flex h-full items-center"
            role="navigation"
            aria-label="주 메뉴"
          >
            {NAV_ITEMS.map((item) => (
              <div
                key={item.label}
                className="relative h-full"
                onMouseEnter={() => setHoveredMenu(item.label)}
                onMouseLeave={() => setHoveredMenu(null)}
              >
                <Link
                  href={item.href}
                  className={`text-xl font-bold px-9 h-full flex items-center transition-colors duration-${
                    HEADER_CONSTANTS.ANIMATION_DURATION.TEXT_COLOR
                  } ${getTextColor()}`}
                >
                  {item.label}
                </Link>
                <DropdownMenu
                  item={item}
                  isVisible={hoveredMenu === item.label}
                />
              </div>
            ))}

            {/* 데스크톱 햄버거 메뉴 버튼 */}
            <div className="-mr-6 h-full hidden lg:flex items-center">
              <button
                onClick={toggleMenu}
                className={`px-6 h-full transition-colors duration-${
                  HEADER_CONSTANTS.ANIMATION_DURATION.HEADER
                } ${getTextColor()}`}
                aria-label={isMenuOpen ? "메뉴 닫기" : "메뉴 열기"}
                aria-expanded={isMenuOpen}
              >
                {isMenuOpen ? (
                  <CloseIcon className="w-6 h-6" />
                ) : (
                  <HamburgerIcon className="w-6 h-6" />
                )}
              </button>
            </div>
          </nav>

          {/* 모바일 햄버거 메뉴 버튼 */}
          <div className="absolute right-5 xl:hidden h-full flex items-center">
            <button
              onClick={toggleMenu}
              className={`transition-colors duration-${HEADER_CONSTANTS.ANIMATION_DURATION.HEADER} text-foreground`}
              aria-label={isMenuOpen ? "메뉴 닫기" : "메뉴 열기"}
              aria-expanded={isMenuOpen}
            >
              {isMenuOpen ? (
                <CloseIcon className="w-6 h-6" />
              ) : (
                <HamburgerIcon className="w-6 h-6" />
              )}
            </button>
          </div>
        </div>

        {/* 모바일 메뉴 영역 */}
        <div
          className={`lg:hidden border-t transition-all duration-${
            HEADER_CONSTANTS.ANIMATION_DURATION.MENU
          } ease-out overflow-hidden border-border ${
            typeof window !== "undefined" && isMenuOpen
              ? "flex-1 opacity-100"
              : "max-h-0 opacity-0"
          }`}
        >
          <div className="py-6 h-full flex flex-col">
            <nav
              className="flex flex-col overflow-y-auto overflow-x-hidden flex-1 pb-24"
              role="navigation"
              aria-label="모바일 메뉴"
            >
              {NAV_ITEMS.map((item, index) => (
                <div key={item.label}>
                  <Link
                    href={item.href}
                    onClick={closeMenu}
                    className={`transition-colors duration-300 block text-xl text-primary font-extrabold mb-6 ${getTextColor()}`}
                  >
                    {item.label}
                  </Link>
                  {item.submenu && (
                    <SubMenu
                      items={item.submenu}
                      isVisible={true}
                      onLinkClick={closeMenu}
                    />
                  )}
                  {index < NAV_ITEMS.length - 1 && (
                    <div className="border-b border-border my-6" />
                  )}
                </div>
              ))}
            </nav>
          </div>
        </div>

        {/* 데스크톱 확장 메뉴 영역 */}
        <div
          className={`hidden lg:block border-t transition-all duration-${
            HEADER_CONSTANTS.ANIMATION_DURATION.MENU
          } ease-out overflow-hidden border-border ${
            typeof window !== "undefined" && isMenuOpen
              ? `max-h-[${HEADER_CONSTANTS.MENU.MAX_HEIGHT}px] opacity-100`
              : "max-h-0 opacity-0"
          }`}
        >
          <div className="py-8 px-6">
            <div className="grid grid-cols-5 gap-8">
              {NAV_ITEMS.map((item, index) => (
                <div
                  key={item.label}
                  className={`space-y-4 ${
                    index < NAV_ITEMS.length - 1
                      ? "border-r border-border pr-8"
                      : ""
                  }`}
                >
                  <h3 className="text-xl font-bold text-primary mb-6">
                    <Link
                      href={item.href}
                      onClick={closeMenu}
                      className="hover:text-primary transition-colors duration-300"
                    >
                      {item.label}
                    </Link>
                  </h3>
                  {item.submenu && (
                    <SubMenu
                      items={item.submenu}
                      isVisible={true}
                      onLinkClick={closeMenu}
                    />
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}
